import { useState } from "react";
import { UseMutateFunction } from "@tanstack/react-query";
import { toast } from "react-toastify";

import {
  CancelVerificationCodeResponse,
  CancelVerificationCodeResponseRequestPayload,
  CodeVerificationSession,
  PhoneNumber,
  RetryVerificationCodeResponse,
  RetryVerificationCodeResponseRequestPayload,
  SendVerificationCodeRequestPayload,
  SendVerificationCodeResponse,
  UpdatePhoneNumberRequestPayload,
  VerifyCodeRequestPayload,
  VerifyCodeResponse,
} from "~/common/types/verificationCode";
import {
  CODE_VERIFICATION_CHANNELS,
  CODE_VERIFICATION_SCOPES,
  INITIAL_PHONE_NUMBER,
  INITIAL_VERIFICATION_SESSION,
} from "../constants/verificationCode";
import { customErrorSchema } from "../types";
import { PhoneVerificationCode } from "./PhoneVerificationCode";
import { PhoneVerificationNumber } from "./PhoneVerificationNumber";

export const PhoneVerification = ({
  userId,
  actions,
  isLoading = false,
  onCancel,
  onSuccess,
}: {
  actions: {
    cancel: UseMutateFunction<
      CancelVerificationCodeResponse,
      Error,
      CancelVerificationCodeResponseRequestPayload,
      unknown
    >;
    retry: UseMutateFunction<
      RetryVerificationCodeResponse,
      Error,
      RetryVerificationCodeResponseRequestPayload,
      unknown
    >;
    send: UseMutateFunction<
      SendVerificationCodeResponse,
      Error,
      SendVerificationCodeRequestPayload,
      unknown
    >;
    update: UseMutateFunction<
      string,
      Error,
      UpdatePhoneNumberRequestPayload,
      unknown
    >;
    verify: UseMutateFunction<
      VerifyCodeResponse,
      Error,
      VerifyCodeRequestPayload,
      unknown
    >;
  };
  isLoading: boolean;
  onCancel?: () => void;
  onSuccess?: () => void;
  userId: string;
}) => {
  const [phoneNumber, setPhoneNumber] =
    useState<PhoneNumber>(INITIAL_PHONE_NUMBER);
  const [verificationSession, setVerificationSession] =
    useState<CodeVerificationSession>(INITIAL_VERIFICATION_SESSION);

  const errorHandler = (error: Error) => {
    const result = customErrorSchema.safeParse(error);

    if (result.success) {
      if (result.data.response.data.detail === "Token expired") {
        toast.error("This session has expired. Please try again.");
        cancelCode();
      } else if (result.data.response.data.detail === "Invalid code") {
        toast.error("This code doesn't match. Please try again.");
      } else {
        toast.error(result.data.response.data.detail);
      }
    }
  };

  const sendCode = (phoneNumber: PhoneNumber) => {
    actions.send(
      {
        phone_number: phoneNumber,
        scope: CODE_VERIFICATION_SCOPES.PHONE_UPDATE,
        channel: CODE_VERIFICATION_CHANNELS.SMS,
      },
      {
        onSuccess: ({ verification_token, can_resend_at }) => {
          setPhoneNumber(phoneNumber);
          setVerificationSession({
            token: verification_token,
            canResendAt: new Date(can_resend_at).getTime(),
            isExpired: false,
          });
        },
        onError: errorHandler,
      },
    );
  };

  const cancelCode = () => {
    if (!verificationSession.token) {
      toast.error("No verification token present");
      return;
    }

    actions.cancel(
      {
        verification_token: verificationSession.token,
      },
      {
        onSettled: () => {
          setPhoneNumber(INITIAL_PHONE_NUMBER);
          setVerificationSession(INITIAL_VERIFICATION_SESSION);
        },
      },
    );
  };

  const completeVerification = (otp: string) => {
    actions.update(
      {
        verification_token: otp,
        user_id: userId,
        phone_number: phoneNumber,
      },
      {
        onSuccess: () => {
          toast.success("Mobile number updated successfully");
          onSuccess?.();
        },
        onError: errorHandler,
      },
    );
  };

  const verifyCode = ({ code }: { code: string }) => {
    if (!verificationSession.token) {
      toast.error("No verification token present");
      return;
    }

    actions.verify(
      {
        verification_code: code,
        verification_token: verificationSession.token,
      },
      {
        onSuccess: (data) => {
          completeVerification(data.one_use_token);
        },
        onError: errorHandler,
      },
    );
  };

  const retryCode = () => {
    if (!verificationSession.token) {
      toast.error("No verification token present");
      return;
    }

    actions.retry(
      {
        verification_token: verificationSession.token,
      },
      {
        onSuccess: ({ verification_token, can_resend_at }) => {
          toast.success("A new security code has been sent");
          setVerificationSession({
            token: verification_token,
            canResendAt: new Date(can_resend_at).getTime(),
            isExpired: false,
          });
        },
        onError: errorHandler,
      },
    );
  };

  if (!phoneNumber.number) {
    return (
      <PhoneVerificationNumber
        isLoading={isLoading}
        onCancel={onCancel}
        onSubmit={sendCode}
      />
    );
  } else {
    return (
      <PhoneVerificationCode
        isLoading={isLoading}
        onCancel={cancelCode}
        onRetry={retryCode}
        onSubmit={verifyCode}
        phoneNumber={phoneNumber}
        verificationSession={verificationSession}
      />
    );
  }
};
